import classcat from "classcat";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@touchtech/web-baselayer-ui";

import {
  LocalStorageKeys,
  PaymentStatus,
  TranslationContext,
} from "@is/constants";
import { LocalStorageService } from "@is/services";
import { sendErrorMessageToSlack } from "@is/utils/slack";

import css from "../PaymentStatusPage.module.css";

const DESKTOP_BREAKPOINT = 800;

type Props = {
  paymentStatus: PaymentStatus;
};

const useManagePaymentStatusPage = ({ paymentStatus }: Props) => {
  const { t } = useTranslation(TranslationContext.Checkout);

  const isDesktop = window.innerWidth > DESKTOP_BREAKPOINT;

  const [errorMessage, setErrorMessage] = useState("");

  const title = useMemo(() => {
    switch (paymentStatus) {
      case PaymentStatus.LinkExpired:
        return (
          <span
            className={classcat([
              css.mainTitle,
              { ["text-headline-5xl"]: isDesktop },
              { ["text-headline-3xl"]: !isDesktop },
            ])}
          >
            {t("Link expired")}
          </span>
        );

      case PaymentStatus.Failure:
        return (
          <span
            className={classcat([
              css.mainTitle,
              { ["text-headline-5xl"]: isDesktop },
              { ["text-headline-3xl"]: !isDesktop },
            ])}
          >
            {errorMessage
              ? t("Oops! Checkout failed")
              : t("Something went wrong")}
          </span>
        );

      case PaymentStatus.Success:
        return (
          <span
            className={classcat([
              css.mainTitle,
              { ["text-headline-5xl"]: isDesktop },
              { ["text-headline-3xl"]: !isDesktop },
            ])}
          >
            {t("Thank you")}
          </span>
        );

      default:
        return (
          <div className={css.spinnerContainer}>
            <Spinner />
          </div>
        );
    }
  }, [isDesktop, paymentStatus, errorMessage, t]);

  const subtitle = useMemo(() => {
    switch (paymentStatus) {
      case PaymentStatus.LinkExpired:
        return (
          <>
            {t(
              "The link you tried to access is no longer valid as it has exceeded the 30-day limit."
            )}
          </>
        );

      case PaymentStatus.Failure:
        return (
          <>
            {errorMessage && (
              <span
                className={classcat([
                  css.errorMessage,
                  { ["text-headline-3xl"]: isDesktop },
                  { ["text-headline-2xl"]: !isDesktop },
                ])}
              >
                {errorMessage}
              </span>
            )}
            <br /> {t("Apologies for the inconvenience.")}
            <br /> {t("For assistance, contact us at")}{" "}
            <span className={css.link}>support@touchtech.com</span>.
          </>
        );

      case PaymentStatus.Success:
        return <>{t("The checkout has been successfully confirmed")}</>;

      default:
        return <>{t("Please wait while creating your basket")}</>;
    }
  }, [paymentStatus, errorMessage, isDesktop, t]);

  useEffect(() => {
    const checkoutErrorMessage = LocalStorageService.getItem(
      LocalStorageKeys.CheckoutErrorMessage
    );

    if (checkoutErrorMessage) {
      setErrorMessage(checkoutErrorMessage);
    }

    LocalStorageService.removeItem(LocalStorageKeys.CheckoutErrorMessage);
  }, []);

  if (paymentStatus === PaymentStatus.Failure) {
    const error = {
      paymentStatus,
    };
    const message = JSON.stringify(error);

    sendErrorMessageToSlack(message);
  }

  return { title, subtitle, isDesktop };
};

export default useManagePaymentStatusPage;
